import { ReactNode, ElementType } from 'react'
import clsx from 'clsx'
import {
  ChatBubbleLeftRightIcon,
  HeartIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { Logo } from './Logo'

export interface AuthValueProp {
  icon: ElementType
  title: string
  description: string
}

export interface AuthShellProps {
  /** Card content (form + supporting UI). */
  children: ReactNode
  /** Label under the wordmark on the hero panel (e.g. "Tenant Portal"). */
  brandSubtitle?: string
  /** Big headline text on the hero panel. */
  heroTitle?: ReactNode
  /** Supporting sentence below the headline. */
  heroTagline?: string
  /** 2–4 value props shown on the hero. Falls back to the LipaFlow story. */
  valueProps?: AuthValueProp[]
  /** Small footnote at the bottom of the hero panel. */
  heroFootnote?: string
  /** Bottom-of-page footer under the auth card (right panel). */
  cardFooter?: ReactNode
  /** Hero gradient. Defaults to a warm teal; `graphite` for admin console. */
  tone?: 'teal' | 'graphite'
  /** Hide the hero panel entirely (single-column layout). */
  compact?: boolean
}

const DEFAULT_VALUE_PROPS: AuthValueProp[] = [
  {
    icon: ChatBubbleLeftRightIcon,
    title: 'A simpler way to pay',
    // Shared by every sector's sign-up and invite pages: no sector's words,
    // and only what the product does today.
    description: 'People see what they owe and pay from their phone, with no app to install.',
  },
  {
    icon: HeartIcon,
    title: 'Keep follow-ups organised',
    description: 'Track outstanding balances and payment plans in the same workspace.',
  },
  {
    icon: ChartBarIcon,
    title: 'A clear financial picture',
    description: 'Review collections, account history and the work that needs attention.',
  },
]

/**
 * Split-panel auth layout used by every LipaFlow portal.
 *
 * Left  hero panel — brand mark, headline, value props on a deep brand
 *                    gradient. Hidden below `lg` so mobile keeps the form
 *                    in the first viewport.
 * Right form panel — `canvas` background, one centred column, the form
 *                    itself on a `surface` panel separated by a hairline.
 *
 * Texture note: the hero uses a large-scale concentric-arc motif (an echo of
 * the wordmark's speech bubble) at ~6% opacity rather than the blurred-blob
 * cliché. It reads as intentional at 1440px and disappears politely at 1024px.
 */
export function AuthShell({
  children,
  brandSubtitle,
  heroTitle,
  heroTagline,
  valueProps,
  heroFootnote,
  cardFooter,
  tone = 'teal',
  compact = false,
}: AuthShellProps) {
  // Brand steps (teal-*/stone-*) are intentional here: this is an
  // illustrative brand surface, not a themed UI surface, and it must stay
  // dark in both light and dark mode so the white logo/text keep contrast.
  const heroGradient =
    tone === 'graphite'
      ? 'bg-[linear-gradient(135deg,#141718_0%,#00343A_55%,#00474E_100%)]'
      : 'bg-[linear-gradient(135deg,#00474E_0%,#006D77_52%,#00343A_100%)]'
  const items = valueProps ?? DEFAULT_VALUE_PROPS

  return (
    <div className="flex min-h-screen bg-canvas">
      {!compact && (
        <aside
          className={clsx(
            'relative hidden lg:flex lg:w-[46%] xl:w-[42%] flex-col justify-between overflow-hidden p-12 xl:p-16',
            heroGradient,
          )}
        >
          <HeroTexture />

          <Logo tone="dark" subtitle={brandSubtitle} className="relative z-10" />

          <div className="relative z-10 max-w-md">
            {heroTitle && (
              <h1 className="font-outfit text-display text-white xl:text-display-xl">
                {heroTitle}
              </h1>
            )}
            {heroTagline && (
              <p className="mt-5 font-inter text-body-lg leading-relaxed text-white/75">
                {heroTagline}
              </p>
            )}

            <ul className="mt-12 space-y-7">
              {items.map((prop) => (
                <li key={prop.title} className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-chip bg-white/10 ring-1 ring-inset ring-white/15">
                    <prop.icon className="h-5 w-5 text-white/90" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block font-outfit text-h4 font-medium text-white">
                      {prop.title}
                    </span>
                    <span className="mt-0.5 block font-inter text-body-sm leading-relaxed text-white/80">
                      {prop.description}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {heroFootnote && (
            <p className="relative z-10 font-inter text-caption text-white/75">{heroFootnote}</p>
          )}
        </aside>
      )}

      {/* Form panel */}
      <main className="auth-form-panel flex min-w-0 flex-1 flex-col items-center justify-center px-5 py-12 sm:px-8">
        <div className={clsx('mb-9 flex items-center gap-3', !compact && 'lg:hidden')}>
          <Logo tone="light" subtitle={brandSubtitle} size="md" />
        </div>
        <div className="w-full max-w-[26rem] animate-fade-in">{children}</div>
        {cardFooter && <div className="mt-7 w-full max-w-[26rem]">{cardFooter}</div>}
      </main>
    </div>
  )
}

/**
 * Concentric-arc motif + a single very soft light source. Kept in one place
 * so the texture cannot drift between portals.
 */
function HeroTexture() {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <svg
        className="absolute -right-1/4 top-1/2 h-[140%] w-auto -translate-y-1/2 opacity-[0.07]"
        viewBox="0 0 400 400"
        fill="none"
      >
        {[80, 130, 180, 230, 280].map((r) => (
          <circle key={r} cx="200" cy="200" r={r} stroke="white" strokeWidth="1.5" />
        ))}
      </svg>
      <div className="absolute -left-24 -top-24 h-[28rem] w-[28rem] rounded-full bg-white/[0.04] blur-3xl" />
    </div>
  )
}

/**
 * Inline error banner used inside auth forms. Extracted so every portal
 * surfaces auth errors identically.
 */
export function AuthError({ message }: { message: string | null | undefined }) {
  if (!message) return null
  return (
    <div
      className="mt-5 flex items-start gap-2.5 rounded-input border border-danger/20 bg-danger-soft px-3.5 py-3 font-inter text-body-sm text-danger"
      role="alert"
    >
      <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  )
}
